import type {
  ChannelModelRecord,
  CreateChannelModelRequest,
  CreateChannelModelResponse,
  DeleteChannelModelResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { listChannelModels, createChannelModel, deleteChannelModel } from './channelModels.ts';

const CM_KEY = ['channel-models'] as const;

export function useChannelModelsList(channelId?: string): UseQueryResult<readonly ChannelModelRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CM_KEY, channelId ?? '__all__'],
    queryFn: async () => {
      const res = await listChannelModels(
        channelId ? { channel_id: channelId } : {},
        fetchImpl,
      );
      return res.channel_models;
    },
  });
}

export function useCreateChannelModel(): UseMutationResult<CreateChannelModelResponse, Error, CreateChannelModelRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateChannelModelRequest) => createChannelModel(body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CM_KEY }); },
  });
}

export function useDeleteChannelModel(): UseMutationResult<
  DeleteChannelModelResponse,
  Error,
  { channelId: string; model: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, model }) =>
      deleteChannelModel(channelId, model, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CM_KEY }); },
  });
}
